import { morph } from '@theme/morph';

class MessageBlock extends HTMLElement {
  connectedCallback() {
    // CRITICAL: Initialize message on page load based on current variant state
    this.initializeMessage();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.addEventListener('variant:update', this.updateMessage);
    document.addEventListener('variant:update', this.updateMessage);
  }

  /**
   * Initialize message on page load based on current variant state
   */
  initializeMessage() {
    const variant = this.getVariantFromJson();
    const defaultMessage = this.dataset.defaultMessage;

    // CRITICAL: Check Liquid's initial state first (for single-variant products)
    // Liquid sets data-show-estimated-delivery="true" when variant is selected and available
    const liquidShowsEstimatedDelivery = this.dataset.showEstimatedDelivery === 'true';

    // Check estimated delivery FIRST - if variant is available, show estimated delivery, not sold out icon
    if (liquidShowsEstimatedDelivery) {
      // Liquid determined we should show estimated delivery (variant is selected and available)
      this.dataset.showEstimatedDelivery = 'true';
      this.updateIconForVariant(variant);
      this.updateEstimatedDelivery();
      return;
    }

    // CRITICAL: Check Liquid's determination of sold out state (for single-variant products)
    // Liquid sets data-is-sold-out="true" when variant is unavailable (including single-variant products)
    const liquidIsSoldOut = this.dataset.isSoldOut === 'true';

    // Check sold out state AFTER checking estimated delivery
    // This handles single-variant products where getVariantFromJson() might return null
    if (liquidIsSoldOut) {
      // Liquid may have already set restock message (data-show-restock-message); don't overwrite it
      const liquidShowRestock = this.dataset.showRestockMessage === 'true';
      const showedRestock = liquidShowRestock ||
        this.updateMessageForUnavailableVariant(variant);
      if (showedRestock) {
        this.updateIconForDefault();
      } else {
        this.updateIconForSoldOut();
      }
      this.dataset.showEstimatedDelivery = 'false';
      return;
    }

    // CRITICAL: Trust Liquid's determination of the message
    // Liquid checks if all variants are unavailable and sets data-default-message accordingly
    // If Liquid set it to sold out, respect that; otherwise use the default message

    if (variant && !variant.available) {
      // Variant is selected and unavailable - update message immediately
      this.updateMessageForUnavailableVariant(variant);
      this.updateIconForVariant(variant);
    } else if (!variant) {
      // No variant selected - trust Liquid's data-default-message
      // Liquid will have set it to sold out if all variants are unavailable
      // Otherwise it will be the default message
      // CRITICAL: Do NOT show estimated delivery when no variant is selected
      // Estimated delivery should only show when a variant is selected and available
      this.dataset.showEstimatedDelivery = 'false';

      // Check if all variants are unavailable to determine icon
      const allVariantsUnavailable = this.#checkAllVariantsUnavailable();
      if (allVariantsUnavailable) {
        this.updateIconForSoldOut();
      } else {
        this.updateIconForDefault();
      }

      const messageText = this.querySelector('.product-form__message-text');
      if (messageText) {
        const textValue = Theme?.translations?.default_message || defaultMessage;
        if (textValue && messageText.textContent !== textValue) {
          messageText.textContent = textValue;
        }
      }

      // Don't show estimated delivery - wait until variant is selected
    } else {
      // Variant is available - show estimated delivery if enabled
      const enableEstimatedDelivery = this.dataset.enableEstimatedDelivery === 'true';
      if (enableEstimatedDelivery && variant && variant.available) {
        this.dataset.showEstimatedDelivery = 'true';
        this.updateIconForVariant(variant);
        this.updateEstimatedDelivery();
      } else {
        this.updateIconForVariant(variant);
      }
    }
  }

  /**
   * Check if all variants are unavailable
   * @returns {boolean}
   */
  #checkAllVariantsUnavailable() {
    const variantPicker = this.closest('.product-details, .shopify-section')?.querySelector('variant-picker');
    if (!variantPicker) return false;

    // Try to get product data from variant picker
    const productId = variantPicker.dataset.productId;
    if (!productId) return false;

    // Check all variant options to see if they're all unavailable
    const variantOptions = variantPicker.querySelectorAll('[data-option-available]');
    if (variantOptions.length === 0) return false;

    // If we have variant options, check if all are unavailable
    let allUnavailable = true;
    for (const option of variantOptions) {
      const available = option.dataset.optionAvailable === 'true';
      if (available) {
        allUnavailable = false;
        break;
      }
    }

    return allUnavailable;
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.removeEventListener('variant:update', this.updateMessage);
    document.removeEventListener('variant:update', this.updateMessage);
  }

  updateEstimatedDelivery() {
    const showEstimatedDelivery = this.dataset.showEstimatedDelivery === 'true';
    if (!showEstimatedDelivery) return;

    const shippingEstimate = parseInt(this.dataset.shippingEstimate || '3');
    const shippingRange = parseInt(this.dataset.shippingRange || '2');

    const start = new Date();
    start.setDate(start.getDate() + shippingEstimate);

    const end = new Date();
    end.setDate(end.getDate() + shippingEstimate + shippingRange);

    const firstDate = start.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    });

    const lastDate = end.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    });

    const messageText = this.querySelector('.product-form__message-text');
    if (messageText) {
      const template = Theme?.translations?.estimated_delivery_template ||
        this.dataset.estimatedDeliveryTemplate ||
        'Estimated delivery {{ first_date }} to {{ last_date }}';

      messageText.textContent = template
        .replace('{{ first_date }}', firstDate)
        .replace('{{ last_date }}', lastDate);
    }
  }

  /**
   * Update message with restock date
   * @param {string} restockDateString - ISO date string (YYYY-MM-DD)
   */
  updateRestockMessage(restockDateString) {
    const messageText = this.querySelector('.product-form__message-text');
    if (!messageText) return;

    const template = Theme?.translations?.restock_template ||
      this.dataset.restockTemplate ||
      'Back in stock on {{ date }}';

    // Format date as DD/MM/YYYY (same as color picker drawer)
    const formattedDate = this.formatRestockDate(restockDateString);
    if (!formattedDate) return;

    messageText.textContent = template.replace('{{ date }}', formattedDate);
  }

  /**
   * Format restock date string (matches color picker drawer logic)
   * @param {string} dateString - ISO date string (YYYY-MM-DD)
   * @returns {string} Formatted date (DD/MM/YYYY) or empty string
   */
  formatRestockDate(dateString) {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '';
    }
  }

  /**
   * Get variant from JSON script tag (similar to low-stock-message.js)
   * Uses the closest variant picker to ensure we get the correct one
   * @returns {Object|null} The variant object or null
   */
  getVariantFromJson() {
    // Find the closest variant picker to this message block to ensure we get the correct one
    const variantPicker = this.closest('.product-details, .shopify-section')?.querySelector('variant-picker');
    if (!variantPicker) return null;

    const scriptTag = variantPicker.querySelector('script[type="application/json"]');
    if (!scriptTag) return null;

    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Morph this message block with the updated version from the fetched HTML
   * This ensures all attributes and content are properly updated
   * @param {Document} [html] - Optional HTML document from the variant update event
   * @returns {boolean} True if morphing was successful, false otherwise
   */
  morphFromFetchedHtml(html) {
    if (!html) return false;

    // Find the message block in the fetched HTML by ID
    // The fetched HTML contains the full product page with the updated message block
    const morphedMessageBlock = html.querySelector(`message-block[id="${this.id}"]`);
    if (!morphedMessageBlock) return false;

    // Morph this element with the new one from the fetched HTML
    // This updates all attributes (including data-default-message) and content
    morph(this, morphedMessageBlock);
    return true;
  }

  /**
   * Handle variant:update events to show estimated delivery or update message text
   * Matches Liquid template logic:
   * - If variant is available → show estimated delivery
   * - If variant is unavailable:
   *   1. Morph message block from fetched HTML (server-calculated, includes custom messages and restock dates)
   *   2. Fallback to client-side calculation if HTML doesn't contain the message
   * @param {CustomEvent} event - The variant update event
   */
  updateMessage = (event) => {
    const enableEstimatedDelivery = this.dataset.enableEstimatedDelivery === 'true';
    const variant = event.detail?.resource;
    const html = event.detail?.data?.html;

    // If variant is available and estimated delivery is enabled, show estimated delivery
    if (enableEstimatedDelivery && variant && variant.available) {
      this.dataset.showEstimatedDelivery = 'true';
      this.updateIconForVariant(variant);
      this.updateEstimatedDelivery();
      return;
    }

    // If variant is unavailable OR no variant selected, clear estimated delivery flag
    if (!variant || (variant && !variant.available)) {
      this.dataset.showEstimatedDelivery = 'false';

      // If variant is unavailable, update message
      if (variant && !variant.available) {
        // First, try to morph this message block from the fetched HTML
        // This ensures all attributes (data-default-message, data-is-sold-out) and content are updated
        // The fetched HTML contains the server-calculated message (custom messages, restock dates, etc.)
        this.morphFromFetchedHtml(html);

        // Check if Liquid set data-is-sold-out after morphing (for single-variant products)
        const liquidIsSoldOut = this.dataset.isSoldOut === 'true';

        // Check if we should show restock message (before updating message text)
        const enableBackInStock = this.dataset.enableBackInStock === 'true';
        const showedRestockMessage = enableBackInStock && this.#tryUpdateRestockMessage(variant);

        // Update message text if restock message wasn't shown
        // The morph might succeed but with wrong content (default message instead of sold out message)
        // This handles cases where:
        // 1. No HTML was fetched (first click, local variant change)
        // 2. The fetched HTML has the wrong message (default/estimated delivery instead of OOS message)
        if (!showedRestockMessage) {
          const messageText = this.querySelector('.product-form__message-text');
          if (messageText) {
            // Always update for OOS variants - morph might succeed but with wrong content
            // Use requestAnimationFrame to ensure variant picker JSON is updated (for local variant changes)
            requestAnimationFrame(() => {
              // Manually calculate and update the message
              this.updateMessageForUnavailableVariant(variant);
            });
          }
        }

        // Update icon based on message type
        // If restock message is shown, use default icon; otherwise use sold out icon
        if (showedRestockMessage) {
          // Show default icon for back-in-stock messages
          this.updateIconForDefault();
        } else if (liquidIsSoldOut || !variant.available) {
          // Show sold out icon only when actually sold out (no restock date)
          this.updateIconForSoldOut();
        } else {
          this.updateIconForVariant(variant);
        }
      } else if (!variant) {
        // No variant selected - check if all variants are unavailable
        const allVariantsUnavailable = this.#checkAllVariantsUnavailable();
        if (allVariantsUnavailable) {
          this.updateIconForSoldOut();
        } else {
          this.updateIconForDefault();
        }

        // Show default message
        const messageText = this.querySelector('.product-form__message-text');
        const defaultMessage = Theme?.translations?.default_message || this.dataset.defaultMessage;
        if (messageText && defaultMessage) {
          messageText.textContent = defaultMessage;
        }
      }
    } else if (variant && variant.available) {
      // Variant is available - show default message if not showing estimated delivery
      const showEstimatedDelivery = this.dataset.showEstimatedDelivery === 'true';
      if (!showEstimatedDelivery) {
        const messageText = this.querySelector('.product-form__message-text');
        const defaultMessage = Theme?.translations?.default_message || this.dataset.defaultMessage;
        if (messageText && defaultMessage) {
          messageText.textContent = defaultMessage;
        }
      }
    }
  };

  /**
   * Check if a restock date is valid and in the future
   * @param {string|null} restockDate - ISO date string (YYYY-MM-DD) or null
   * @returns {boolean} True if date is valid and in the future
   */
  #isValidFutureRestockDate(restockDate) {
    if (!restockDate || typeof restockDate !== 'string') {
      return false;
    }

    const restockDateObj = new Date(restockDate);
    const now = new Date();

    return restockDateObj > now && !isNaN(restockDateObj.getTime());
  }

  /**
   * Get restock date from variant picker's selected option
   * @returns {string|null} Restock date string or null
   */
  #getRestockDateFromPicker() {
    const variantPicker = this.closest('.product-details, .shopify-section')?.querySelector('variant-picker');
    if (!variantPicker) return null;

    // Try radio inputs first
    let selectedInput = variantPicker.querySelector('input[type="radio"]:checked');
    // If no radio, try select options
    if (!selectedInput) {
      const selectedOption = variantPicker.querySelector('select option[selected]');
      if (selectedOption) {
        selectedInput = selectedOption;
      }
    }

    if (selectedInput && selectedInput instanceof HTMLElement) {
      return selectedInput.dataset.restockDate || null;
    }

    return null;
  }

  /**
   * Get restock date from variant JSON data
   * @returns {string|null} Restock date string or null
   */
  #getRestockDateFromJson() {
    const variantData = this.getVariantFromJson();
    if (variantData && typeof variantData === 'object' && 'restock_date' in variantData) {
      const restockDate = variantData.restock_date;
      return typeof restockDate === 'string' ? restockDate : null;
    }
    return null;
  }

  /**
   * Try to update message with restock date if available
   * @param {Object|null} variant - Optional variant object from event
   * @returns {boolean} True if restock message was applied, false otherwise
   */
  #tryUpdateRestockMessage(variant = null) {
    let restockDate = null;

    // When variant is from the event (user just changed selection), only use that variant's data.
    // Do not fall back to picker/JSON – they can be stale (e.g. still S when user selected XS).
    if (variant && typeof variant === 'object') {
      restockDate = typeof variant.restock_date === 'string' ? variant.restock_date : null;
      if (this.#isValidFutureRestockDate(restockDate)) {
        this.updateRestockMessage(restockDate);
        return true;
      }
      return false;
    }

    // No variant from event (e.g. init): use picker then JSON
    restockDate = this.#getRestockDateFromPicker();
    if (!restockDate) {
      restockDate = this.#getRestockDateFromJson();
    }

    if (this.#isValidFutureRestockDate(restockDate)) {
      this.updateRestockMessage(restockDate);
      return true;
    }

    return false;
  }

  /**
   * Update message for unavailable variant
   * Shows restock date if available and enabled, otherwise shows sold out message
   * @param {Object} variant - The variant object from the event
   * @returns {boolean} True if restock message was shown, false if sold out message was shown
   */
  updateMessageForUnavailableVariant(variant) {
    const messageText = this.querySelector('.product-form__message-text');
    if (!messageText) return false;

    // Check if back in stock is enabled
    const enableBackInStock = this.dataset.enableBackInStock === 'true';

    // Try to show restock date if enabled and available (pass variant to check restock_date property)
    if (enableBackInStock && this.#tryUpdateRestockMessage(variant)) {
      return true; // Restock message was shown
    }

    // Fallback: Show sold out message
    // Get the sold out message from translations (product.form.sold_out_message)
    let soldOutMessage = Theme?.translations?.sold_out_message || 'Sold Out'; // Safe minimal fallback

    if (Theme?.translations) {
      const translations = Theme.translations;
      const product = translations['product'];
      const nestedMessage = product && typeof product === 'object' && 'form' in product
        ? (product['form'] && typeof product['form'] === 'object' ? product['form']['sold_out_message'] : undefined)
        : undefined;
      const flatMessage = translations['product.form.sold_out_message'];

      soldOutMessage = translations['sold_out_message'] ?? nestedMessage ?? flatMessage ?? soldOutMessage;
    }

    messageText.textContent = soldOutMessage;
    return false; // Sold out message was shown
  }

  /**
   * Update icon based on variant availability
   * Shows sold out icon when variant is unavailable, default icon otherwise
   * @param {Object|null} variant - The variant object or null
   */
  updateIconForVariant(variant) {
    if (!variant || !variant.available) {
      this.updateIconForSoldOut();
    } else {
      this.updateIconForDefault();
    }
  }

  /**
   * Update icon to show sold out icon (out-of-stock_1.png)
   */
  updateIconForSoldOut() {
    let iconContainer = this.querySelector('.product-form__message-icon');

    if (!iconContainer) {
      // Create icon container if it doesn't exist
      iconContainer = document.createElement('div');
      iconContainer.className = 'product-form__message-icon';
      const messageText = this.querySelector('.product-form__message-text');
      if (messageText) {
        this.insertBefore(iconContainer, messageText);
      } else {
        this.appendChild(iconContainer);
      }
    }

    // Remove any existing content
    iconContainer.innerHTML = '';

    // Create and insert the sold out image
    const soldOutIconUrl = this.dataset.soldOutIconUrl;
    if (soldOutIconUrl) {
      const img = document.createElement('img');
      img.className = 'product-form__sold-out-icon';
      img.src = soldOutIconUrl;
      img.alt = 'Out of stock';
      iconContainer.appendChild(img);
    }
  }

  /**
   * Update icon to show default icon (from block settings)
   */
  updateIconForDefault() {
    const iconContainer = this.querySelector('.product-form__message-icon');
    const defaultIcon = this.dataset.defaultIcon;

    if (!defaultIcon) {
      // No default icon set - remove icon container
      if (iconContainer) {
        iconContainer.remove();
      }
      return;
    }

    if (!iconContainer) {
      // Create icon container if it doesn't exist
      const newIconContainer = document.createElement('div');
      newIconContainer.className = 'product-form__message-icon';
      const messageText = this.querySelector('.product-form__message-text');
      if (messageText) {
        this.insertBefore(newIconContainer, messageText);
      } else {
        this.appendChild(newIconContainer);
      }
      newIconContainer.innerHTML = defaultIcon;
      return;
    }

    // Remove sold out class and restore default icon
    iconContainer.className = 'product-form__message-icon';
    iconContainer.innerHTML = defaultIcon;
  }
}

if (!customElements.get('message-block')) {
  customElements.define('message-block', MessageBlock);
}
